import React, { useMemo } from 'react';
import _ from 'underscore';
import { Constants } from '@bingads-webui-clientcenter/entity-utils';
import { i18nKeys } from '@bingads-webui-react/file-picker';
import { StringUtils } from '@bingads-webui-clientcenter/common-utils';
import { TaxTypeFormPropTypes } from '../shapes';
import { GlobalTaxForm } from './global-taxform';
import { CustomValidateItems } from '../validator';
import { EmptyTaxForm } from './empty-taxform';
import { registerTaxform } from '../taxform-store';
import { THTaxViewTaxForm } from './viewtaxforms/thailand-viewtaxform';
import { EmptyViewTaxForm } from './viewtaxforms/empty-viewtaxform';
import { addLegalIdentifierNumberInTaxSchema, getPilotStatus, withViewTaxFormWithFilledEmptyTaxValue, withTaxFormByPilot, getTaxTypeLabel } from '../utils';
import { GlobalTaxIdHelpPopUpId, FileScenarioMapping, GlobalFilePickerRequiredErrorMessage, LegalIdentifierHintI18nKey, LegalIdentifierNumberPilot, GlobalTaxIdCountryCodeToPilots } from '../constants';

const {
  TaxTHVatNumberRegExp, TaxType, TaxCertLabel, TaxLabelFormat, CountryCode,
} = Constants;
const { regexTest } = StringUtils;
const taxIdValidator = _.partial(regexTest, TaxTHVatNumberRegExp);

const taxSchemaGetter = ({ permissions }) => {
  const IsLegalIdentifierNumberPilotOn = getPilotStatus(LegalIdentifierNumberPilot, permissions);

  const taxSchema = {
    name: 'Thailand Taxation Validation Schema',
    type: 'object',
    properties: {
      [TaxType.VatNumber]: {
        type: 'string',
        required: false,
        helpPopupTopic: GlobalTaxIdHelpPopUpId,
      },
    },
  };

  if (IsLegalIdentifierNumberPilotOn) {
    addLegalIdentifierNumberInTaxSchema(taxSchema);
  }

  return taxSchema;
};

export const THTaxForm = ({
  i18n,
  onValidate,
  showLineByLineFormGroup,
  showTaxFilePicker,
  alwaysShowError,
  data,
  mtData,
  taxCertification,
  mtTaxCertification,
  urlBuilder,
  disabled,
  permissions,
}) => {
  const taxCertLabel = useMemo(() => i18n.getString(TaxCertLabel.TaxCertificate), [i18n]);
  const taxTypeLabel = useMemo(() => getTaxTypeLabel(TaxType.VatNumber, i18n, { countryCode: CountryCode.TH }), [i18n]);
  const vatValidatorErrorMessage = useMemo(() => i18n.getString(CustomValidateItems.GlobalThailandVATFormatValidation), [i18n]);
  const vatLabel = useMemo(() => getTaxTypeLabel(TaxType.VatNumber, i18n, { labelFormat: TaxLabelFormat.RequiredForBusiness, countryCode: CountryCode.TH }), [i18n]);
  const customStrings = useMemo(() => ({
    [i18nKeys.FilePicker_ErrorMessage_Required]: i18n.getString(GlobalFilePickerRequiredErrorMessage, {
      taxId: taxTypeLabel,
    }),
  }), [i18n, taxTypeLabel]);

  return (
    <GlobalTaxForm
      i18n={i18n}
      onValidate={onValidate}
      taxSchemaGetter={taxSchemaGetter}
      vatLabel={vatLabel}
      customStrings={customStrings}
      showLineByLineFormGroup={showLineByLineFormGroup}
      vatValidatorErrorMessage={vatValidatorErrorMessage}
      showTaxFilePicker={showTaxFilePicker}
      alwaysShowError={alwaysShowError}
      data={data}
      mtData={mtData}
      taxCertification={taxCertification}
      mtTaxCertification={mtTaxCertification}
      urlBuilder={urlBuilder}
      disabled={disabled}
      taxIdValidator={taxIdValidator}
      taxCertLabel={taxCertLabel}
      taxCertScenario={FileScenarioMapping.TH}
      showLegalIdentifierNumberField
      legalIdentifierHint={i18n.getString(LegalIdentifierHintI18nKey[CountryCode.TH])}
      permissions={permissions}
    />
  );
};

THTaxForm.propTypes = TaxTypeFormPropTypes;

const globalPilotFlag = GlobalTaxIdCountryCodeToPilots.TH;

const TaxForm = withTaxFormByPilot({
  pilotFlag: globalPilotFlag,
  PilotForm: THTaxForm,
  DefaultForm: EmptyTaxForm,
});

const ViewTaxForm = withTaxFormByPilot({
  pilotFlag: globalPilotFlag,
  PilotForm: THTaxViewTaxForm,
  DefaultForm: EmptyViewTaxForm,
});

registerTaxform('/TH/', TaxForm, withViewTaxFormWithFilledEmptyTaxValue({ ViewTaxForm, getTaxSchema: taxSchemaGetter }));
